/**
 * Clients Context
 * 
 * Manages client-related state to prevent unnecessary re-renders
 * across unrelated features when client data changes.
 */

import React, { createContext, useContext, useState, useMemo, useCallback, useEffect } from 'react';

const ClientsContext = createContext();

export const ClientsProvider = ({ children }) => {
  // Core client data
  const [clients, setClients] = useState([]); // Paginated/filtered list for Client Management display
  const [allClientsForLookup, setAllClientsForLookupRaw] = useState([]); // Full list for lookups throughout app
  
  // Performance: Ref mirror for synchronous lookups without re-render dependency.
  // Components that only READ allClientsForLookup (e.g., findClientById) can use 
  // the ref instead of the state, avoiding cascading re-renders when the list updates.
  const allClientsForLookupRef = React.useRef([]);
  const setAllClientsForLookup = React.useCallback((valueOrUpdater) => {
    setAllClientsForLookupRaw(prev => {
      const next = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev) : valueOrUpdater;
      allClientsForLookupRef.current = next;
      return next;
    });
  }, []);
  const [selectedClient, setSelectedClient] = useState(null);
  const [showClientDetail, setShowClientDetail] = useState(false);
  const [clientDetailLoading, setClientDetailLoading] = useState(false); // Loading state for client chart transition
  const [clientsLoading, setClientsLoading] = useState(false); // Loading state for filter changes
  
  // Client list filters (arrays to match Dashboard usage)
  const [clientStatusFilter, setClientStatusFilter] = useState(['active']); // Default to showing active clients
  const [clientStatusFilterMode, setClientStatusFilterMode] = useState('include');
  const [clientProviderFilter, setClientProviderFilter] = useState([]); // Array for multi-select
  const [clientProviderFilterMode, setClientProviderFilterMode] = useState('include');
  const [clientLabels, setClientLabels] = useState([]);
  const [selectedLabelFilter, setSelectedLabelFilter] = useState([]);
  const [selectedLabelFilterMode, setSelectedLabelFilterMode] = useState('include');
  // Insurance filter (multiselect): array of company names + the synthetic
  // "__self_pay__" sentinel for clients with no active insurance. Mirrors
  // the backend `insurance_filter` query param.
  const [selectedInsuranceFilter, setSelectedInsuranceFilter] = useState([]);
  const [selectedInsuranceFilterMode, setSelectedInsuranceFilterMode] = useState('include');
  
  // Client list UI state
  const [clientSearch, setClientSearch] = useState('');
  const [clientViewMode, setClientViewMode] = useState('card'); // 'card' or 'table'
  const [useCompactClientView, setUseCompactClientView] = useState(true); // Use new compact view
  const [clientsPerPage, setClientsPerPage] = useState(20);
  const [clientsCurrentPage, setClientsCurrentPage] = useState(1);
  const [clientsSortColumn, setClientsSortColumn] = useState('full_name');
  const [clientsSortDirection, setClientsSortDirection] = useState('asc');
  const [clientNameFormat, setClientNameFormat] = useState('first_last'); // 'first_last' or 'last_first'
  
  // Server-side pagination state
  const [clientsTotalCount, setClientsTotalCount] = useState(0);
  const [clientsTotalPages, setClientsTotalPages] = useState(0);
  const [clientsLastUpdated, setClientsLastUpdated] = useState(null); // Track when clients were last refreshed
  
  // Client detail view state
  const [clientDetailTab, setClientDetailTab] = useState('overview');
  const [clientHeaderCollapsed, setClientHeaderCollapsed] = useState(false);
  
  // Client chart data (loaded when viewing a specific client)
  const [clientNotes, setClientNotes] = useState([]); // Client-specific notes (Option A: newest 100 initially)
  // Option A pagination state: have we fetched all the client's notes yet?
  // `clientNotes` starts as the newest 100; `hasMore=true` means more exist
  // in the DB beyond what's in state. Flipping to `false` happens either
  // (a) the initial fetch returned <100 (client has no older notes), or
  // (b) the "load all" fetch completed successfully.
  const [clientNotesHasMore, setClientNotesHasMore] = useState(false);
  const [clientNotesTotalCount, setClientNotesTotalCount] = useState(0);
  const [clientDocuments, setClientDocuments] = useState([]);
  const [clientDiagnoses, setClientDiagnoses] = useState([]);
  const [clientMedications, setClientMedications] = useState([]);
  const [clientAppointments, setClientAppointments] = useState([]); // Client's appointment history
  const [clientAuthorizations, setClientAuthorizations] = useState([]);
  const [clientEligibility, setClientEligibility] = useState(null);
  const [clientAlerts, setClientAlerts] = useState([]);
  const [clientContacts, setClientContacts] = useState([]);
  const [clientReleases, setClientReleases] = useState([]);
  const [clientVitals, setClientVitals] = useState([]);
  const [clientGroups, setClientGroups] = useState([]);
  const [clientMDTReviews, setClientMDTReviews] = useState([]);
  const [clientEnrollments, setClientEnrollments] = useState([]);
  const [clientInsurances, setClientInsurances] = useState([]);
  const [clientBillingSummary, setClientBillingSummary] = useState(null);
  const [clientClaims, setClientClaims] = useState([]);
  const [clientPaymentHistory, setClientPaymentHistory] = useState([]);
  const [clientServiceEnrollments, setClientServiceEnrollments] = useState([]);
  
  // Memoized filtered lists (use allClientsForLookup for accurate counts)
  const pendingIntakeClients = useMemo(() => 
    (allClientsForLookup || []).filter(c => c.status === 'pending_intake'), 
    [allClientsForLookup]
  );
  
  const waitlistStatusClients = useMemo(() => 
    (allClientsForLookup || []).filter(c => c.status === 'waitlist'), 
    [allClientsForLookup]
  );
  
  const activeClients = useMemo(() => 
    (allClientsForLookup || []).filter(c => c.status === 'active'), 
    [allClientsForLookup]
  );
  
  const nonArchivedClients = useMemo(() => 
    (allClientsForLookup || []).filter(c => c.status !== 'archived'), 
    [allClientsForLookup]
  );

  // Clear client chart data (when switching clients or closing detail view)
  const clearClientChartData = useCallback(() => {
    // Close the client chart and clear selected client
    setSelectedClient(null);
    setShowClientDetail(false);
    setClientDetailLoading(false);
    setClientDetailTab('overview');
    
    // Clear all client-specific data
    setClientNotes([]);
    setClientNotesHasMore(false);
    setClientNotesTotalCount(0);
    setClientDocuments([]);
    setClientDiagnoses([]);
    setClientMedications([]);
    setClientAppointments([]);
    setClientAuthorizations([]);
    setClientEligibility(null);
    setClientAlerts([]);
    setClientContacts([]);
    setClientReleases([]);
    setClientVitals([]);
    setClientGroups([]);
    setClientMDTReviews([]);
    setClientEnrollments([]);
    setClientInsurances([]);
    setClientBillingSummary(null);
    setClientClaims([]);
    setClientPaymentHistory([]);
    setClientServiceEnrollments([]);
  }, []);

  // Feb 2026 — ensureClientsLoaded()
  //
  // Used by the V2 route-split shell.  When a user navigates directly
  // to /v2/<some-tab> without first landing on the legacy /dashboard,
  // ClientsContext has never been populated → tables that resolve
  // client_id → client.full_name show blank rows and search dialogs
  // return zero results.
  //
  // Idempotent: short-circuits when the lookup list already exists.
  // First batch (1000 clients) paints immediately so the UI is
  // usable; remaining pages stream in the background via
  // setTimeout(...0).  Matches the legacy Dashboard's
  // `fetchAllClientsForLookup` behaviour exactly so production
  // practices with >1000 clients see EVERYONE in the list.
  //
  // We deliberately do NOT call this from ClientsProvider mount
  // because legacy Dashboard already triggers a richer fetch with
  // filters; firing both would race.
  // Tracks whether the FULL client list has been loaded (all pages).
  // A non-empty allClientsForLookup is NOT proof of that — e.g. the
  // appointment form merges in a small provider-filtered batch, which
  // previously made ensureClientsLoaded short-circuit and left billing
  // dropdowns with "a handful of clients" (Jun 2026 bug).
  const fullClientsLoadedRef = React.useRef(false);
  const clientsLoadPromiseRef = React.useRef(null);

  // Jun 2026 — label-catalog self-heal: the catalog previously only
  // loaded via silent one-shot fetches; a failed/empty load left
  // billing-label dots blank on some rows. Retries with backoff, and a
  // window-focus listener refetches whenever the catalog is empty.
  const clientLabelsRef = React.useRef([]);
  useEffect(() => { clientLabelsRef.current = clientLabels; }, [clientLabels]);
  const labelsFetchInFlightRef = React.useRef(false);

  const ensureClientLabelsLoaded = useCallback(async (force = false) => {
    if (labelsFetchInFlightRef.current) return clientLabelsRef.current;
    if (!force && clientLabelsRef.current.length > 0) return clientLabelsRef.current;
    labelsFetchInFlightRef.current = true;
    try {
      const axios = (await import('axios')).default;
      const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
      for (let attempt = 0; attempt < 3; attempt += 1) {
        try {
          const r = await axios.get(`${API}/client-labels`);
          if (Array.isArray(r.data)) {
            setClientLabels(r.data);
            return r.data;
          }
        } catch (err) {
          console.error(`[ClientsContext] client-labels fetch failed (attempt ${attempt + 1}/3):`, err?.message || err);
          await new Promise((res) => setTimeout(res, 700 * (attempt + 1)));
        }
      }
      return clientLabelsRef.current;
    } finally {
      labelsFetchInFlightRef.current = false;
    }
  }, []);

  useEffect(() => {
    const onFocus = () => {
      if (clientLabelsRef.current.length === 0) ensureClientLabelsLoaded();
    };
    window.addEventListener('focus', onFocus);
    return () => window.removeEventListener('focus', onFocus);
  }, [ensureClientLabelsLoaded]);

  const ensureClientsLoaded = useCallback(async () => {
    if (fullClientsLoadedRef.current) {
      return allClientsForLookupRef.current;
    }
    if (clientsLoadPromiseRef.current) {
      return clientsLoadPromiseRef.current;
    }
    const axios = (await import('axios')).default;
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

    // Merge fetched minimal rows over any partial list already in
    // state, preserving extra fields (e.g. assigned_providers) that
    // other flows may have attached to existing entries.
    const mergeIntoLookup = (fetched) => {
      setAllClientsForLookup((prev) => {
        if (!prev || prev.length === 0) return fetched;
        const byId = new Map();
        prev.forEach((c) => { if (c?.id) byId.set(c.id, c); });
        fetched.forEach((c) => {
          if (!c?.id) return;
          const existing = byId.get(c.id);
          byId.set(c.id, existing ? { ...existing, ...c } : c);
        });
        return Array.from(byId.values());
      });
    };

    const load = (async () => {
      try {
        // ─── First batch (foreground): immediate paint ─────────────
        const firstResp = await axios.get(
          `${API}/clients?page_size=1000&fields=minimal&include_archived=true`
        );
        const firstData = firstResp.data;
        let firstBatch = [];
        let totalCount = 0;
        if (Array.isArray(firstData)) {
          firstBatch = firstData;
          totalCount = firstBatch.length;
        } else if (firstData?.clients) {
          firstBatch = firstData.clients;
          totalCount =
            firstData.pagination?.total_count
            ?? firstData.total_count
            ?? firstBatch.length;
        }
        mergeIntoLookup(firstBatch);
        setClients((prev) => (prev && prev.length > 0 ? prev : firstBatch));

        // ─── Remaining pages (background): paginate to totalCount ──
        if (firstBatch.length < totalCount) {
          setTimeout(async () => {
            try {
              let all = [...firstBatch];
              let page = 2;
              const pageSize = 1000;
              // Defensive ceiling — 50 × 1000 = 50,000 clients.
              while (all.length < totalCount && page <= 50) {
                const r = await axios.get(
                  `${API}/clients?page=${page}&page_size=${pageSize}&fields=minimal&include_archived=true`
                );
                let pageClients = [];
                if (Array.isArray(r.data)) pageClients = r.data;
                else if (r.data?.clients) pageClients = r.data.clients;
                if (pageClients.length === 0) break;
                all = [...all, ...pageClients];
                page += 1;
              }
              // Single state update after all pages loaded → avoids N re-renders.
              mergeIntoLookup(all);
              setClients((prev) => (prev && prev.length > all.length ? prev : all));
              fullClientsLoadedRef.current = true;
            } catch (bgErr) {
              console.error('[ClientsContext] background pagination failed:', bgErr);
            }
          }, 0);
        } else {
          fullClientsLoadedRef.current = true;
        }
        return firstBatch;
      } catch (error) {
        console.error('[ClientsContext] ensureClientsLoaded failed:', error);
        return [];
      }
    })();

    clientsLoadPromiseRef.current = load;
    try {
      return await load;
    } finally {
      clientsLoadPromiseRef.current = null;
    }
  }, [setAllClientsForLookup]);

  // Phase-2 stabilization (Feb 2026): memoize the Provider value so
  // consumers don't re-render every time ClientsProvider re-renders.
  // This context is the heaviest in the app — it holds the full client
  // list, all chart-tab data (notes, claims, diagnoses, etc.), and 50+
  // state fields total.  Without the memo, ANY change (e.g., a filter
  // toggle, a search keystroke) forced a fresh `value` object literal,
  // which propagated through Radix Dialog focus traps and Dashboard's
  // ~85k-line JSX tree.
  //
  // Setters from useState are stable across renders — intentionally
  // NOT listed in the deps array.  `setAllClientsForLookup`,
  // `clearClientChartData`, and the memoized filtered lists are
  // already useCallback/useMemo-stable above, so they only change
  // when their own underlying state moves.
  const value = useMemo(() => ({
    // Core data
    clients,
    setClients,
    allClientsForLookup,
    allClientsForLookupRef,
    setAllClientsForLookup,
    selectedClient,
    setSelectedClient,
    showClientDetail,
    setShowClientDetail,
    clientDetailLoading,
    setClientDetailLoading,
    clientsLoading,
    setClientsLoading,
    
    // Filters
    clientStatusFilter,
    setClientStatusFilter,
    clientStatusFilterMode,
    setClientStatusFilterMode,
    clientProviderFilter,
    setClientProviderFilter,
    clientProviderFilterMode,
    setClientProviderFilterMode,
    clientLabels,
    setClientLabels,
    selectedLabelFilter,
    setSelectedLabelFilter,
    selectedLabelFilterMode,
    setSelectedLabelFilterMode,
    selectedInsuranceFilter,
    setSelectedInsuranceFilter,
    selectedInsuranceFilterMode,
    setSelectedInsuranceFilterMode,
    
    // Client list UI
    clientSearch,
    setClientSearch,
    clientViewMode,
    setClientViewMode,
    useCompactClientView,
    setUseCompactClientView,
    clientsPerPage,
    setClientsPerPage,
    clientsCurrentPage,
    setClientsCurrentPage,
    clientsSortColumn,
    setClientsSortColumn,
    clientsSortDirection,
    setClientsSortDirection,
    clientNameFormat,
    setClientNameFormat,
    
    // Server-side pagination
    clientsTotalCount,
    setClientsTotalCount,
    clientsTotalPages,
    setClientsTotalPages,
    clientsLastUpdated,
    setClientsLastUpdated,
    
    // Client detail view
    clientDetailTab,
    setClientDetailTab,
    clientHeaderCollapsed,
    setClientHeaderCollapsed,
    
    // Client chart data
    clientNotes,
    setClientNotes,
    clientNotesHasMore,
    setClientNotesHasMore,
    clientNotesTotalCount,
    setClientNotesTotalCount,
    clientDocuments,
    setClientDocuments,
    clientDiagnoses,
    setClientDiagnoses,
    clientMedications,
    setClientMedications,
    clientAppointments,
    setClientAppointments,
    clientAuthorizations,
    setClientAuthorizations,
    clientEligibility,
    setClientEligibility,
    clientAlerts,
    setClientAlerts,
    clientContacts,
    setClientContacts,
    clientReleases,
    setClientReleases,
    clientVitals,
    setClientVitals,
    clientGroups,
    setClientGroups,
    clientMDTReviews,
    setClientMDTReviews,
    clientEnrollments,
    setClientEnrollments,
    clientInsurances,
    setClientInsurances,
    clientBillingSummary,
    setClientBillingSummary,
    clientClaims,
    setClientClaims,
    clientPaymentHistory,
    setClientPaymentHistory,
    clientServiceEnrollments,
    setClientServiceEnrollments,
    
    // Computed
    pendingIntakeClients,
    waitlistStatusClients,
    activeClients,
    nonArchivedClients,
    
    // Actions
    clearClientChartData,
    ensureClientsLoaded,
    ensureClientLabelsLoaded,
  }), [
    // Core
    clients, allClientsForLookup, selectedClient, showClientDetail,
    clientDetailLoading, clientsLoading,
    // Filters
    clientStatusFilter, clientStatusFilterMode,
    clientProviderFilter, clientProviderFilterMode,
    clientLabels, selectedLabelFilter, selectedLabelFilterMode,
    selectedInsuranceFilter, selectedInsuranceFilterMode,
    // UI
    clientSearch, clientViewMode, useCompactClientView,
    clientsPerPage, clientsCurrentPage,
    clientsSortColumn, clientsSortDirection, clientNameFormat,
    // Pagination
    clientsTotalCount, clientsTotalPages, clientsLastUpdated,
    // Detail
    clientDetailTab, clientHeaderCollapsed,
    // Chart data
    clientNotes, clientNotesHasMore, clientNotesTotalCount,
    clientDocuments, clientDiagnoses, clientMedications,
    clientAppointments, clientAuthorizations, clientEligibility,
    clientAlerts, clientContacts, clientReleases, clientVitals,
    clientGroups, clientMDTReviews, clientEnrollments, clientInsurances,
    clientBillingSummary, clientClaims, clientPaymentHistory,
    clientServiceEnrollments,
    // Computed + actions (already memoized — included for correctness)
    pendingIntakeClients, waitlistStatusClients, activeClients, nonArchivedClients,
    setAllClientsForLookup, clearClientChartData, ensureClientsLoaded, ensureClientLabelsLoaded,
  ]);

  return (
    <ClientsContext.Provider value={value}>
      {children}
    </ClientsContext.Provider>
  );
};

export const useClients = () => {
  const context = useContext(ClientsContext);
  if (!context) {
    throw new Error('useClients must be used within ClientsProvider');
  }
  return context;
};

export default ClientsContext;
